import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Check, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { DateQuickPicks } from "@/components/ui/date-quick-picks";
import {
  IssueStatusPill,
  issueStatusLabel,
} from "@/components/issues/issue-status-pill/issue-status-pill";
import { usePostIssueUpdate } from "@/hooks/use-issue-mutations";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ApiError } from "@/lib/api";
import { UnsupportedImageError } from "@/lib/heic/ensure-renderable";
import { formatIssueNumber } from "@/lib/issues";
import { cn } from "@/lib/utils";
import { SELECTABLE_ISSUE_STATUSES } from "@/types/api";
import type { ProjectIssue, SelectableIssueStatus } from "@/types/api";
import {
  ISSUE_CAMERA_QUERY,
  isInsideIssueCamera,
  isIssueCameraOpen,
} from "./issue-camera-marker";
import { IssueUpdateCamera } from "./issue-update-camera";
import { prepareIssuePhoto } from "./prepare-issue-photo";

/**
 * Which form the sheet wears.
 *
 * `reopen` is not a second write path — it posts the same update, with
 * `in_progress` prescribed instead of asked for. See [[issue-update-sheet.spec]].
 */
export type IssueUpdateMode = "update" | "reopen";

/**
 * What a posted update says about itself, once the sheet has closed.
 *
 * The reopen form says "reopened" whichever status it posted — the person did
 * one thing, and where the issue went is already on the issue. A close reads as
 * a verb rather than "marked Closed". Every other status reuses the pill's
 * label, so a wire value never becomes a word in a second place. See
 * [[issue-update-sheet.spec]] § Confirmation toast.
 */
function postedMessage(
  issue: ProjectIssue,
  status: SelectableIssueStatus,
  reopening: boolean,
): string {
  const subject = `Issue ${formatIssueNumber(issue.number)}`;
  if (reopening) return `${subject} reopened`;
  if (status === "closed") return `${subject} closed`;
  return `${subject} marked ${issueStatusLabel(status)}`;
}

// The empty squares that add photos. Hover firms the outline, fills with the
// card hover wash and inks the icon (via `group`); like every hover here it
// only applies where a pointer can hover. See [[issue-update-sheet.spec]] § Hover.
const PHOTO_SQUARE =
  "group flex h-20 w-20 cursor-pointer items-center justify-center rounded-md border border-border bg-card transition-colors hover:border-foreground/30 hover:bg-card-hover";

interface IssueUpdateSheetProps {
  projectId: string;
  issue: ProjectIssue;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether this user may set `closed`. Mirrors the backend rule. */
  canClose: boolean;
  /**
   * Whether this user may set `in_progress` and `completed` — the Update verb.
   * Someone who may only close (the raiser, or a Close holder) is offered
   * `closed` alone. Mirrors the backend rule.
   */
  canUpdate: boolean;
  /** Defaults to the ordinary update form. */
  mode?: IssueUpdateMode;
  /**
   * `dialog` centres the same form in a popup — the desktop Issues list, where
   * a sheet across a wide monitor lands far from the status pill that opened
   * it. Defaults to the bottom sheet. See [[issue-update-sheet.spec]].
   */
  presentation?: "drawer" | "dialog";
  /**
   * Name the issue under the title. For hosts that open the sheet over a list
   * rather than over the issue itself, where nothing else on screen says which
   * issue is being updated.
   */
  showIssue?: boolean;
  /**
   * Open with the camera already up, for a host whose button was a camera —
   * the issue page's Add progress photo tile. Done leaves the sheet open with
   * the shots; closing the camera without keeping one closes the sheet too.
   * Ignored where the sheet offers no camera. See [[issue-update-sheet.spec]]
   * § Photos.
   */
  startWithCamera?: boolean;
}

/**
 * The only way anything about an issue changes after it is raised.
 *
 * Remarks, photos and a status, in one post. There is no separate submit,
 * approve or reject — those were the same operation with a different status and
 * a different caller, which is why this sheet is the whole write surface. See
 * [[issues.spec]].
 *
 * Reopening a closed issue rides the same sheet for the same reason: it is an
 * update posting `in_progress`, so a second surface would only duplicate this
 * one. The `reopen` mode prescribes that status instead of asking for it.
 */
export function IssueUpdateSheet({
  projectId,
  issue,
  open,
  onOpenChange,
  canClose,
  canUpdate,
  mode = "update",
  presentation = "drawer",
  showIssue = false,
  startWithCamera = false,
}: IssueUpdateSheetProps) {
  /*
    Keyed on `open` (and the mode, which the action bar swaps under us the
    moment a close or a reopen lands) so the form REMOUNTS each time the
    sheet opens: fresh text, no leftover photos from an abandoned attempt,
    and a default status that follows the issue's current state. Syncing
    that with an effect would be setState during an effect, which cascades
    renders.
  */
  const form = (
    <UpdateForm
      key={`${open ? "open" : "closed"}-${mode}`}
      projectId={projectId}
      issue={issue}
      canClose={canClose}
      canUpdate={canUpdate}
      mode={mode}
      presentation={presentation}
      showIssue={showIssue}
      startWithCamera={startWithCamera}
      onClose={() => onOpenChange(false)}
    />
  );

  /*
    The camera renders outside the sheet's DOM (see IssueUpdateCamera), so to
    the sheet every tap on it is a press outside — which would dismiss the
    sheet and, through the key above, wipe everything typed. Those presses,
    and Escape while the camera is up, are refused here.
  */
  const keepOpenUnderCamera = (event: {
    target: EventTarget | null;
    preventDefault: () => void;
  }) => {
    if (isInsideIssueCamera(event.target)) event.preventDefault();
  };
  const keepOpenOnEscape = (event: KeyboardEvent) => {
    if (isIssueCameraOpen()) event.preventDefault();
  };

  if (presentation === "dialog") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* `bare`: the form lays out its own header, scrolling body and
            action bar, exactly as it does inside the drawer. */}
        <DialogContent
          bare
          aria-describedby={undefined}
          className="sm:max-w-md"
          onPointerDownOutside={keepOpenUnderCamera}
          onEscapeKeyDown={keepOpenOnEscape}
        >
          {form}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="max-h-[90vh]"
        onPointerDownOutside={keepOpenUnderCamera}
        onEscapeKeyDown={keepOpenOnEscape}
      >
        {form}
      </DrawerContent>
    </Drawer>
  );
}

interface UpdateFormProps {
  projectId: string;
  issue: ProjectIssue;
  canClose: boolean;
  canUpdate: boolean;
  mode: IssueUpdateMode;
  presentation: "drawer" | "dialog";
  showIssue: boolean;
  startWithCamera: boolean;
  onClose: () => void;
}

function UpdateForm({
  projectId,
  issue,
  canClose,
  canUpdate,
  mode,
  presentation,
  showIssue,
  startWithCamera,
  onClose,
}: UpdateFormProps) {
  const reopening = mode === "reopen";
  // The title must belong to whichever primitive is hosting the form — each
  // one wires its own title to its own accessible name.
  const Header = presentation === "dialog" ? DialogHeader : DrawerHeader;
  const Title = presentation === "dialog" ? DialogTitle : DrawerTitle;

  // Defaults to where the issue already stands, so posting a plain progress
  // note costs no thought. It is still SENT — the trail is a complete state
  // log with no "unchanged" hole in it. `assigned` is never selectable, so an
  // untouched issue defaults to the first real step instead.
  //
  // Reopening starts at `in_progress` — the common case, and the only status a
  // closed issue can move to that is not already where it stands. `completed`
  // stays on offer for work that was finished but closed too early.
  //
  // Someone who may only close has one status to offer, so it starts there —
  // defaulting to one they may not post would make the primary button a 403.
  // See [[issue-update-sheet.spec]].
  const [status, setStatus] = useState<SelectableIssueStatus>(
    !canUpdate && !reopening
      ? "closed"
      : reopening || issue.status === "assigned"
        ? "in_progress"
        : issue.status,
  );
  const [remarks, setRemarks] = useState("");
  // Always blank, never seeded from `issue.due_date`. A field that opened
  // showing the deadline would read as an edit of the deadline, which is
  // precisely what it is not — see [[issue-update-sheet.spec]] § Target date.
  const [targetDate, setTargetDate] = useState<string | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const postUpdate = usePostIssueUpdate();

  // The camera square is for phones and tablets. A desktop webcam is not a
  // tool for re-shooting a defect on site. See [[issue-update-sheet.spec]].
  const canUseCamera = useMediaQuery(ISSUE_CAMERA_QUERY);
  const [cameraOpen, setCameraOpen] = useState(false);
  // A host can open the form with the camera already up. Read once: the form
  // remounts on every open, so this is that opening's choice. While that
  // camera is the one up, backing out of it backs out of the form too: the
  // person tapped a camera, not "Update". Done or the library hands over to
  // the form, and from then on its camera square behaves as usual. See
  // [[issue-update-sheet.spec]] § Photos.
  const [openedIntoCamera, setOpenedIntoCamera] = useState(
    () => startWithCamera && canUseCamera,
  );
  // Raised once the sheet is on screen, not in the same commit: a modal sheet
  // hides everything outside itself from assistive tech as it opens, and a
  // camera mounted alongside it was hidden with the rest — it worked by touch
  // but had no name for a screen reader. Mounted after, it is left alone, as
  // it is when the square opens it.
  useEffect(() => {
    if (openedIntoCamera) setCameraOpen(true);
  }, [openedIntoCamera]);

  // Every photo, camera or library, is resized before it joins the row — see
  // prepareIssuePhoto. One at a time, through one queue: the row keeps the
  // order photos were added in, and a phone never holds more than one 12 MP
  // decode in memory at once. Each photo lands as soon as it is ready.
  const [preparing, setPreparing] = useState(0);
  const prepareQueue = useRef<Promise<void>>(Promise.resolve());
  const addPhotos = (files: File[]) => {
    if (files.length === 0) return;
    setPreparing((count) => count + files.length);
    prepareQueue.current = prepareQueue.current.then(async () => {
      for (const file of files) {
        try {
          const photo = await prepareIssuePhoto(file);
          setPhotos((current) => [...current, photo]);
        } catch (error) {
          // Left out rather than posted as bytes nothing can draw. The others
          // in the same pick still land.
          setErrorMessage(
            error instanceof UnsupportedImageError
              ? error.message
              : "Couldn't read one of those photos.",
          );
        } finally {
          setPreparing((count) => count - 1);
        }
      }
    });
  };

  // Derived during render rather than pushed into state by an effect. The
  // cleanup revokes the previous batch whenever the selection changes and on
  // unmount; without it every re-pick leaks a blob for the life of the tab.
  const previews = useMemo(
    () => photos.map((file) => URL.createObjectURL(file)),
    [photos],
  );
  useEffect(
    () => () => previews.forEach((url) => URL.revokeObjectURL(url)),
    [previews],
  );

  // Reopening never offers `closed` — picking it from a closed issue would be a
  // no-op posted as an update. Otherwise each status appears only for someone
  // who may set it: `closed` behind the close rule, the other two behind the
  // posting rule (`canPostIssueUpdate`). Absent rather than disabled, like
  // every gated option.
  const options = SELECTABLE_ISSUE_STATUSES.filter((option) =>
    option === "closed" ? !reopening && canClose : canUpdate,
  );

  // A target date on work being called done or shut has nothing to describe.
  const showTargetDate = status === "in_progress";

  const submit = () => {
    setErrorMessage(null);
    postUpdate.mutate(
      {
        projectId,
        issueId: issue.id,
        status,
        // Exactly what was typed. Nothing is prepended to it — a target is a
        // date the update carries as a date, and remarks someone can type by
        // hand must stay distinguishable from one. See
        // [[issue-update-sheet.spec]] § Target date.
        remarks,
        // Its own field, and discarded along with the section when the status
        // is not `in_progress`: a date chosen and then abandoned must not ride
        // an update about work being called done.
        targetDate: showTargetDate ? targetDate : null,
        photos,
      },
      {
        onSuccess: () => {
          onClose();
          // Raised here, not by a host, so the Issues list and an issue's own
          // page confirm identically and an update never toasts twice. No Undo:
          // an update is appended and never retracted. The row may also leave
          // the list on the refetch (its new status no longer matches the
          // tab), and this is what says it went through.
          toast(postedMessage(issue, status, reopening), {
            icon: <Check className="h-4 w-4" />,
          });
        },
        onError: (error) => {
          // A 403 here is a real answer, not a failure: it means this user may
          // not move the issue to that status. Saying "something went wrong"
          // would send them looking for a bug that isn't there.
          const apiStatus = error instanceof ApiError ? error.status : null;
          if (apiStatus === 403) {
            setErrorMessage("You don't have permission to set that status.");
            return;
          }
          // Likewise a refused reopen. The backend has treated `closed` as
          // terminal in the past; if it still rejects the transition, that is
          // an answer about this issue, not about the connection.
          if (reopening && (apiStatus === 400 || apiStatus === 409)) {
            setErrorMessage("This issue can't be reopened.");
            return;
          }
          setErrorMessage(
            reopening
              ? "Couldn't reopen this issue. Check your connection and try again."
              : "Couldn't post that update. Check your connection and try again.",
          );
        },
      },
    );
  };

  return (
    <>
      <Header
        className={cn(presentation === "dialog" && "p-4 pr-12 text-left")}
      >
        <Title>{reopening ? "Reopen issue" : "Update"}</Title>
        {showIssue && (
          <p className="min-w-0 truncate text-sm text-muted-foreground">
            {formatIssueNumber(issue.number)} &mdash; {issue.title}
          </p>
        )}
        {reopening && (
          <p className="text-sm text-muted-foreground">
            Reopen issue to In Progress or Completed status.
          </p>
        )}
      </Header>

      <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-4">
        <section className="flex flex-col gap-2">
          <span className="text-sm font-medium">Status</span>
          <div className="flex flex-col gap-1.5">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setStatus(option)}
                aria-pressed={status === option}
                className={cn(
                  "group flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors",
                  // Hover: an unselected row previews the raised chip it becomes
                  // when picked; the selected row only firms its outline, since
                  // a dimmer fill would read as being deselected. Tailwind's
                  // hover variant only applies where a pointer can hover, so
                  // touch never shows it. See [[issue-update-sheet.spec]] § Hover.
                  status === option
                    ? "border-foreground/20 bg-card shadow-sm hover:border-foreground/40"
                    : "border-border bg-transparent hover:border-foreground/20 hover:bg-card",
                )}
              >
                {/*
                  The colour rides a dot on every option, not a fill on the
                  selected one. Filling the selection with its own status
                  colour made `closed` — deliberately the quietest treatment —
                  disappear at the moment it was chosen.
                */}
                <IssueStatusPill status={option} variant="dot" />
                <span
                  className={cn(
                    "h-4 w-4 rounded-full border",
                    status === option
                      ? "border-[5px] border-foreground"
                      : "border-border transition-colors group-hover:border-foreground/50",
                  )}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
        </section>

        {showTargetDate && (
          <section className="flex flex-col gap-2">
            <span className="text-sm font-medium">Target date</span>
            <DatePicker
              value={targetDate}
              onChange={setTargetDate}
              placeholder="No target date"
              heading="Target date"
              clearLabel="Clear target date"
              aria-label="Target date"
              className="cursor-pointer transition-colors hover:border-foreground/30 hover:bg-card-hover"
            />
            {/* The same three offsets the raise sheet offers under Due date —
                see [[date-quick-picks]] for why they are one component. The
                chips are shared with the other date fields, which have no hover
                yet, so this sheet reaches them from the row rather than the
                component changing under every host. */}
            <DateQuickPicks
              value={targetDate}
              onChange={setTargetDate}
              className="[&>button:not(:disabled)]:cursor-pointer [&>button:not(:disabled)]:hover:border-foreground/30 [&>button:not(:disabled)]:hover:text-foreground"
            />
          </section>
        )}

        <section className="flex flex-col gap-2">
          <label htmlFor="issue-remarks" className="text-sm font-medium">
            {reopening ? "Why is this being reopened?" : "Remarks"}
          </label>
          <Textarea
            id="issue-remarks"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            rows={3}
            // Outline only: a dark field is recessed (`field`), so lifting its
            // fill would read as a raised card. The dark edge has its own class
            // on the Textarea, which a plain `hover:` loses to, hence both.
            // Focus keeps its own border and ring: Tailwind orders focus-visible
            // after hover, so typing in the field is never overridden by it.
            className="transition-[color,box-shadow,border-color] hover:border-foreground/30 dark:hover:border-foreground/30"
          />
        </section>

        <section className="flex flex-col gap-2">
          <span className="text-sm font-medium">Photos</span>
          <div className="flex flex-wrap gap-2">
            {previews.map((preview, index) => (
              <div key={preview} className="relative">
                <img
                  src={preview}
                  alt=""
                  className="h-20 w-20 rounded-md object-cover"
                />
                <button
                  type="button"
                  aria-label="Remove photo"
                  onClick={() =>
                    setPhotos((current) =>
                      current.filter((_, i) => i !== index),
                    )
                  }
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-foreground text-background transition-colors hover:bg-foreground/80"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {preparing > 0 && (
              <div
                role="status"
                aria-label="Preparing photo…"
                className="flex h-20 w-20 items-center justify-center rounded-md border border-border bg-card"
              >
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
            {canUseCamera && (
              <button
                type="button"
                onClick={() => setCameraOpen(true)}
                aria-label="Take photos"
                className={PHOTO_SQUARE}
              >
                <Camera className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-foreground" />
              </button>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Add photos"
              className={PHOTO_SQUARE}
            >
              <Plus className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-foreground" />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              addPhotos(Array.from(event.target.files ?? []));
              // Reset so picking the same file twice in a row still fires.
              event.target.value = "";
            }}
          />
          {cameraOpen && (
            <IssueUpdateCamera
              projectId={projectId}
              issue={issue}
              onDone={(shots) => {
                setCameraOpen(false);
                setOpenedIntoCamera(false);
                addPhotos(shots);
              }}
              onCancel={() => {
                setCameraOpen(false);
                if (openedIntoCamera) onClose();
              }}
              onUseLibrary={() => {
                // Synchronously, inside the tap: iOS only opens a file picker
                // from a user gesture. The input lives in the sheet, so it is
                // still mounted while the camera closes.
                fileInputRef.current?.click();
                setCameraOpen(false);
                setOpenedIntoCamera(false);
              }}
            />
          )}
        </section>

        {errorMessage && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
      </div>

      <div className="pb-sheet-actions flex gap-2 border-t border-border p-4">
        <Button
          variant="outline"
          className="basis-[38%]"
          onClick={onClose}
          disabled={postUpdate.isPending}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={submit}
          // Not while photos are still being prepared: posting then would
          // silently leave them out.
          disabled={postUpdate.isPending || preparing > 0}
        >
          {reopening
            ? postUpdate.isPending
              ? "Reopening…"
              : "Reopen"
            : postUpdate.isPending
              ? "Posting…"
              : "Update"}
        </Button>
      </div>
    </>
  );
}
